import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAsyncData } from "@/lib/useAsyncData";
import { ReportLoading } from "@/components/leasing/ReportState";
import SimpleImportWizard from "@/components/SimpleImportWizard";
import PolicyAllocationDialog from "@/components/prepaid/PolicyAllocationDialog";
import Pagination from "@/components/Pagination";
import { fmtNumber, fmtDate } from "@/lib/format";
import { Upload, Search, FileText, RotateCcw } from "lucide-react";

const POLICIES_CFG = {
  title: "Import polițe (T2)",
  helpAnchor: "import-polite",
  templatePath: "/prepaid/template/policies", templateName: "Sablon_Polite.xlsx",
  validatePath: "/prepaid/import/policies/validate", confirmPath: "/prepaid/import/confirm",
  previewBase: "/prepaid/import/preview",
  columns: [["ext_id", "ID", "text"], ["registru", "Registru", "text"], ["insurer", "Asigurator", "text"],
            ["nr_polita", "Nr poliță", "text"], ["valoare", "Valoare", "num"], ["moneda", "Moneda", "text"],
            ["data_inceput", "Data început", "date"], ["nr_zile", "Nr zile", "text"]],
  modes: [["add", "Adaugă polițe noi"], ["replace", "Înlocuiește pe ID existent"]],
};
const RETURNS_CFG = {
  title: "Import polițe returnate (T3)",
  helpAnchor: "import-polite",
  templatePath: "/prepaid/template/returns", templateName: "Sablon_Polite_Returnate.xlsx",
  validatePath: "/prepaid/import/returns/validate", confirmPath: "/prepaid/import/confirm",
  previewBase: "/prepaid/import/preview",
  columns: [["nr_polita", "Nr poliță", "text"], ["valoare_returnata", "Valoare returnată", "num"],
            ["data_returnare", "Data returnare", "date"]],
  modes: [["add", "Adaugă returnări"], ["replace", "Înlocuiește toate returnările"]],
};

export default function PolicyList({ registru }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [wizard, setWizard] = useState(null); // 'policies' | 'returns'
  const [detail, setDetail] = useState(null);
  const pageSize = 50;

  const { loading, data, reload } = useAsyncData(async () => {
    const res = await api.get("/prepaid/policies", { params: { registru, search, page, page_size: pageSize } });
    return res.data;
  }, [registru, search, page]);

  const policies = data?.policies || [];
  const total = data?.total || 0;

  const onSearch = (v) => { setSearch(v); setPage(1); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="policy-search-input" placeholder="Caută ID, nr poliță, asigurător…" value={search} onChange={(e) => onSearch(e.target.value)} className="pl-8 w-80" />
        </div>
        <div className="flex gap-2">
          <Button data-testid="import-policies-btn" onClick={() => setWizard("policies")} className="bg-slate-900 text-white gap-2"><Upload className="h-4 w-4" /> Import polițe</Button>
          <Button data-testid="import-returns-btn" onClick={() => setWizard("returns")} variant="outline" className="gap-2"><RotateCcw className="h-4 w-4" /> Import returnări</Button>
        </div>
      </div>

      {loading && !data ? <ReportLoading label="Se încarcă polițele…" /> : (
        <>
        <div className="border rounded-md overflow-auto bg-card max-h-[68vh]">
          <table className="w-full text-sm jh-table" data-testid="policy-list-table">
            <thead className="sticky top-0 bg-slate-100">
              <tr>
                <th className="text-left px-3 py-2">ID</th>
                <th className="text-left px-3 py-2">Asigurător</th>
                <th className="text-left px-3 py-2">Nr poliță</th>
                <th className="text-left px-3 py-2">Data plată</th>
                <th className="text-right px-3 py-2">Valoare</th>
                <th className="text-left px-3 py-2">Mon.</th>
                <th className="text-right px-3 py-2">Valoare RON</th>
                <th className="text-right px-3 py-2">Val. zilnică</th>
                <th className="text-left px-3 py-2">Început</th>
                <th className="text-left px-3 py-2">Sfârșit</th>
                <th className="text-center px-3 py-2">Zile</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => (
                <tr key={p.id} className={`hover:bg-amber-50/40 ${p.superseded ? "opacity-50" : ""}`} data-testid={`policy-row-${p.ext_id}`}>
                  <td className="px-3 py-1.5 font-medium">{p.ext_id} {p.superseded && <Badge variant="secondary" className="ml-1 text-[10px]">înlocuită</Badge>}</td>
                  <td className="px-3 py-1.5">{p.insurer}</td>
                  <td className="px-3 py-1.5">{p.nr_polita}</td>
                  <td className="px-3 py-1.5 tabular">{fmtDate(p.data_plata)}</td>
                  <td className="px-3 py-1.5 text-right tabular">{fmtNumber(p.valoare)}</td>
                  <td className="px-3 py-1.5">{p.moneda}</td>
                  <td className="px-3 py-1.5 text-right tabular">{fmtNumber(p.value_ron)}</td>
                  <td className="px-3 py-1.5 text-right tabular">{fmtNumber(p.daily_value, 4)}</td>
                  <td className="px-3 py-1.5 tabular">{fmtDate(p.data_inceput)}</td>
                  <td className="px-3 py-1.5 tabular">{fmtDate(p.end_date)}</td>
                  <td className="px-3 py-1.5 text-center tabular">{p.nr_zile}</td>
                  <td className="px-3 py-1.5 text-right">
                    <Button size="sm" variant="ghost" className="gap-1 text-slate-700" data-testid={`open-allocation-${p.ext_id}`} onClick={() => setDetail(p)}><FileText className="h-4 w-4" /> Repartizare</Button>
                  </td>
                </tr>
              ))}
              {policies.length === 0 && !loading && (
                <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-400">Nicio poliță. Importați registrul pentru a începe.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} label="polițe" testid="policy-pagination" />
        </>
      )}

      {wizard === "policies" && <SimpleImportWizard open onOpenChange={(v) => !v && setWizard(null)} onDone={reload} config={POLICIES_CFG} />}
      {wizard === "returns" && <SimpleImportWizard open onOpenChange={(v) => !v && setWizard(null)} onDone={reload} config={RETURNS_CFG} />}
      {detail && <PolicyAllocationDialog policy={detail} open onOpenChange={(v) => !v && setDetail(null)} />}
    </div>
  );
}
